import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import * as endpoints from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import type { Plan } from "@/api/types";

const PLAN_DESCRIPTIONS: Record<Plan, string> = {
  light: "Light — 入門プラン。基本商品のみ購入可",
  standard: "Standard — 主力プラン。多くの商品が購入可",
  premium: "Premium — 全商品が購入可、各種システム利用可（Shopee 連携など）",
};

export function ApplicationPage() {
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [desiredPlan, setDesiredPlan] = useState<Plan>("standard");
  const [businessDescription, setBusinessDescription] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await endpoints.submitApplication({
        company_name: companyName.trim(),
        contact_name: contactName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        desired_plan: desiredPlan,
        business_description: businessDescription.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, "申込の送信に失敗しました"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-xl card p-8 space-y-4 text-center">
          <div className="text-5xl">✓</div>
          <h1 className="text-2xl font-semibold">お申込みを受け付けました</h1>
          <p className="text-sm text-gray-600">
            担当者が内容を確認の上、登録いただいたメールアドレス宛にご連絡いたします。
            審査には数営業日お時間をいただく場合がございます。
          </p>
          <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-900 text-left">
            <strong>メールが届かない場合は、迷惑メールフォルダもご確認ください。</strong>
            <br />
            送信元は <span className="font-mono">noreply@can-tan.com</span> です。
          </div>
          <div className="pt-4">
            <Link to="/login" className="btn-secondary inline-block">
              ログイン画面へ
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-brand-700">かんたん在庫</h1>
          <p className="mt-2 text-sm text-gray-600">
            越境EC事業者様向け B2B 仕入れプラットフォーム
          </p>
        </div>

        <div className="card p-6">
          <h2 className="text-xl font-semibold mb-1">新規お申込み</h2>
          <p className="text-sm text-gray-600 mb-4">
            下記フォームに必要事項をご記入ください。審査後、担当者よりご連絡いたします。
          </p>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="company_name">
                企業名 <span className="text-red-600">*</span>
              </label>
              <input
                id="company_name"
                className="input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="label" htmlFor="contact_name">
                ご担当者名 <span className="text-red-600">*</span>
              </label>
              <input
                id="contact_name"
                className="input"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                required
                disabled={submitting}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label" htmlFor="email">
                  メールアドレス <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="label" htmlFor="phone">
                  電話番号
                </label>
                <input
                  id="phone"
                  type="tel"
                  className="input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="desired_plan">
                ご希望プラン <span className="text-red-600">*</span>
              </label>
              <select
                id="desired_plan"
                className="input"
                value={desiredPlan}
                onChange={(e) => setDesiredPlan(e.target.value as Plan)}
                disabled={submitting}
              >
                <option value="light">{PLAN_DESCRIPTIONS.light}</option>
                <option value="standard">{PLAN_DESCRIPTIONS.standard}</option>
                <option value="premium">{PLAN_DESCRIPTIONS.premium}</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                プランは後から変更可能です。初回はご希望のプランでご案内します。
              </p>
            </div>
            <div>
              <label className="label" htmlFor="business">
                事業内容（任意）
              </label>
              <textarea
                id="business"
                className="input h-24 resize-none"
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                placeholder="例: 越境EC（Amazon US / Shopify）で化粧品・日用品を取り扱っています。"
                disabled={submitting}
              />
            </div>
            <div className="border-t pt-4 flex gap-3 justify-between items-center">
              <Link to="/login" className="text-sm text-gray-600 hover:underline">
                ← ログイン画面へ戻る
              </Link>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "送信中..." : "申込を送信"}
              </button>
            </div>
          </form>
        </div>

        <p className="text-center text-xs text-gray-500">
          送信した内容は弊社プライバシーポリシーに従い、審査・ご連絡のみに利用いたします。
        </p>
      </div>
    </div>
  );
}
